'use client';

import React, { useState } from 'react';
import { 
  X, 
  Plus, 
  Clock, 
  User, 
  Scissors, 
  ShoppingBag, 
  Check, 
  DollarSign,
  Calendar,
  Tag
} from 'lucide-react';

export interface TodayAppointment {
  id: string;
  clientName: string;
  clientPhone: string;
  serviceId: string;
  serviceName: string;
  staffId: string;
  staffName: string;
  startTime: string;
  price: number;
  depositAmount: number;
  paymentStatus: string;
  status: string;
}

interface ChargeClientModalProps {
  isOpen: boolean;
  onClose: () => void;
  dateLabel: string;
  todayAppointments: TodayAppointment[];
  onOpenLooseAppointment: () => void;
  onChargeAppointment: (
    appointment: TodayAppointment,
    channel: string,
    voucherCode?: string,
    discount?: number
  ) => Promise<void>;
  onChargeProducts: (payload: any) => Promise<void>;
  products: any[];
}

export default function ChargeClientModal({
  isOpen,
  onClose,
  dateLabel,
  todayAppointments,
  onOpenLooseAppointment,
  onChargeAppointment,
  onChargeProducts,
  products
}: ChargeClientModalProps) {
  const [activeTab, setActiveTab] = useState<'TURNOS' | 'PRODUCTOS'>('TURNOS');
  
  // Estado para cobro rápido de turno agendado
  const [selectedAppt, setSelectedAppt] = useState<TodayAppointment | null>(null);
  const [selectedChannel, setSelectedChannel] = useState('EFECTIVO');
  const [submitting, setSubmitting] = useState(false);

  // Vouchers / Cupones
  const [voucherCode, setVoucherCode] = useState('');
  const [validatingVoucher, setValidatingVoucher] = useState(false);
  const [appliedVoucher, setAppliedVoucher] = useState<{ code: string; discountAmount: number; discountType: string } | null>(null);
  const [voucherError, setVoucherError] = useState('');

  // Estado para venta de productos rápida
  const [productCart, setProductCart] = useState<Array<{ id: string; name: string; price: number; quantity: number }>>([]);
  const [productClientName, setProductClientName] = useState('');
  const [productChannel, setProductChannel] = useState('EFECTIVO');

  if (!isOpen) return null;

  const handleValidateVoucher = async () => {
    if (!voucherCode.trim() || !selectedAppt) return;
    setValidatingVoucher(true);
    setVoucherError('');
    try {
      const res = await fetch('/api/vouchers/validate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code: voucherCode.trim().toUpperCase(),
          total: selectedAppt.price
        })
      });
      const data = await res.json();
      if (data.valid) {
        setAppliedVoucher({
          code: data.code,
          discountAmount: data.discountAmount,
          discountType: data.discountType
        });
      } else {
        setVoucherError(data.error || 'Cupón inválido o no aplicable.');
      }
    } catch (e: any) {
      setVoucherError('Error validando cupón.');
    } finally {
      setValidatingVoucher(false);
    }
  };

  const finalApptPrice = selectedAppt
    ? Math.max(0, selectedAppt.price - (appliedVoucher?.discountAmount || 0))
    : 0;

  const handleConfirmApptCharge = async () => {
    if (!selectedAppt) return;
    setSubmitting(true);
    try {
      await onChargeAppointment(
        selectedAppt,
        selectedChannel,
        appliedVoucher?.code,
        appliedVoucher?.discountAmount || 0
      );
      setSelectedAppt(null);
      setAppliedVoucher(null);
      setVoucherCode('');
      onClose();
    } catch (err) {
      console.error('Error cobrando turno:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddProduct = (p: any) => {
    const idx = productCart.findIndex(item => item.id === p.id);
    if (idx >= 0) {
      const updated = [...productCart];
      updated[idx].quantity += 1;
      setProductCart(updated);
    } else {
      setProductCart([...productCart, { id: p.id, name: p.name, price: p.salePrice, quantity: 1 }]);
    }
  };

  const handleRemoveProduct = (id: string) => {
    setProductCart(productCart.filter(item => item.id !== id));
  };

  const handleConfirmProductSale = async () => {
    if (productCart.length === 0) return;
    setSubmitting(true);
    try {
      await onChargeProducts({
        clientName: productClientName || 'Cliente mostrador',
        paymentChannel: productChannel,
        items: productCart.map(item => ({
          productId: item.id,
          name: item.name,
          quantity: item.quantity,
          unitPrice: item.price
        }))
      });
      setProductCart([]);
      setProductClientName('');
      onClose();
    } catch (err) {
      console.error('Error cobrando productos:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const unchargedAppointments = todayAppointments.filter(
    a => a.paymentStatus !== 'paid_in_full' && a.status !== 'cancelled'
  );

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl max-w-lg w-full max-h-[92vh] overflow-hidden shadow-2xl flex flex-col border border-slate-100 animate-in fade-in zoom-in-95 duration-150">
        
        {/* Cabecera del Modal */}
        <div className="p-6 pb-4 border-b border-slate-100 flex items-start justify-between">
          <div>
            <h2 className="text-xl font-bold text-slate-900 tracking-tight">
              Cobrar cliente
            </h2>
            <p className="text-sm font-medium text-slate-400 mt-0.5">
              {dateLabel}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-xl hover:bg-slate-100 text-slate-400 hover:text-slate-700 transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6 overflow-y-auto space-y-5">
          {/* Sub-paso: Si está cobrando un turno específico agendado */}
          {selectedAppt ? (
            <div className="space-y-4">
              <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200/80">
                <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">
                  Cobrar Turno Agendado
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-base font-bold text-slate-900">{selectedAppt.clientName}</h4>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {selectedAppt.serviceName} · {selectedAppt.startTime} hs · Atiende {selectedAppt.staffName}
                    </p>
                  </div>
                  <div className="text-right">
                    <span className="text-xs text-slate-400 block">Total</span>
                    <span className="text-lg font-black text-emerald-700">
                      ${selectedAppt.price.toLocaleString('es-AR')}
                    </span>
                  </div>
                </div>
              </div>

              {/* Selector de Canal de Cobro */}
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-2">
                  Medio de Pago
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {['EFECTIVO', 'DEBITO', 'CREDITO', 'TRANSFERENCIA', 'MERCADOPAGO', 'OTRO'].map((ch) => (
                    <button
                      key={ch}
                      type="button"
                      onClick={() => setSelectedChannel(ch)}
                      className={`p-2.5 rounded-xl text-xs font-bold border transition text-center ${
                        selectedChannel === ch
                          ? 'bg-[#047857] border-[#047857] text-white shadow-sm'
                          : 'bg-white border-slate-200 text-slate-700 hover:border-slate-300'
                      }`}
                    >
                      {ch}
                    </button>
                  ))}
                </div>
              </div>

              {/* Sección Voucher / Descuento */}
              <div className="pt-2 border-t border-slate-100">
                <label className="block text-xs font-bold text-slate-600 mb-1.5 flex items-center gap-1.5">
                  <Tag className="w-3.5 h-3.5 text-emerald-600" />
                  <span>Cupón / Voucher de Descuento (Opcional)</span>
                </label>

                {appliedVoucher ? (
                  <div className="p-2.5 rounded-xl bg-emerald-50 border border-emerald-200 flex items-center justify-between text-xs">
                    <span className="font-bold text-emerald-800">
                      ✓ Cupón {appliedVoucher.code} ({appliedVoucher.discountType === 'PERCENT' ? 'Porcentaje' : 'Fijo'})
                    </span>
                    <span className="font-black text-emerald-700">
                      -${appliedVoucher.discountAmount.toLocaleString('es-AR')}
                    </span>
                  </div>
                ) : (
                  <div className="space-y-1">
                    <div className="flex items-center gap-1.5">
                      <input
                        type="text"
                        value={voucherCode}
                        onChange={(e) => setVoucherCode(e.target.value)}
                        placeholder="Ej: PRIMERAVEZ o BARBAVIP"
                        className="flex-1 px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold uppercase text-slate-800 placeholder-slate-400 focus:outline-none focus:border-emerald-600"
                      />
                      <button
                        type="button"
                        onClick={handleValidateVoucher}
                        disabled={validatingVoucher || !voucherCode.trim()}
                        className="px-3 py-1.5 bg-emerald-800 hover:bg-emerald-900 text-white text-xs font-bold rounded-xl transition disabled:opacity-50 cursor-pointer"
                      >
                        {validatingVoucher ? '...' : 'Aplicar'}
                      </button>
                    </div>
                    {voucherError && (
                      <span className="text-[11px] font-semibold text-rose-600 block">
                        {voucherError}
                      </span>
                    )}
                  </div>
                )}
              </div>

              <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setSelectedAppt(null)}
                  className="px-4 py-2 rounded-xl text-xs font-bold text-slate-600 hover:bg-slate-100 transition"
                >
                  Volver
                </button>
                <button
                  type="button"
                  disabled={submitting}
                  onClick={handleConfirmApptCharge}
                  className="px-5 py-2.5 bg-[#047857] hover:bg-[#065f46] text-white text-xs font-bold rounded-xl shadow-md transition disabled:opacity-50"
                >
                  {submitting ? 'Confirmando...' : `Confirmar Cobro $${finalApptPrice.toLocaleString('es-AR')}`}
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Botón Destacado: Turno Suelto (Borde punteado verde esmeralda idéntico a AppGenda) */}
              <div
                onClick={() => {
                  onClose();
                  onOpenLooseAppointment();
                }}
                className="p-4 rounded-2xl border-2 border-dashed border-emerald-500/80 hover:border-emerald-600 bg-emerald-50/20 hover:bg-emerald-50/40 cursor-pointer transition flex items-center gap-4 group"
              >
                <div className="w-11 h-11 rounded-full border border-emerald-500/60 bg-white flex items-center justify-center text-emerald-700 group-hover:scale-105 transition shadow-sm shrink-0">
                  <Plus className="w-5 h-5 stroke-[2.5]" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-slate-900 group-hover:text-emerald-900 transition">
                    Turno suelto
                  </h3>
                  <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">
                    Cobrar un turno que no estaba agendado, a un cliente registrado o nuevo.
                  </p>
                </div>
              </div>

              {/* Pestañas de selector: Turnos Agendados de Hoy vs Venta de Productos */}
              <div className="flex items-center gap-2 border-b border-slate-100 pb-2">
                <button
                  onClick={() => setActiveTab('TURNOS')}
                  className={`text-xs font-bold pb-1 transition border-b-2 ${
                    activeTab === 'TURNOS'
                      ? 'border-[#047857] text-[#047857]'
                      : 'border-transparent text-slate-400 hover:text-slate-600'
                  }`}
                >
                  Turnos de hoy ({unchargedAppointments.length})
                </button>
                <button
                  onClick={() => setActiveTab('PRODUCTOS')}
                  className={`text-xs font-bold pb-1 transition border-b-2 ${
                    activeTab === 'PRODUCTOS'
                      ? 'border-[#047857] text-[#047857]'
                      : 'border-transparent text-slate-400 hover:text-slate-600'
                  }`}
                >
                  Venta de productos ({products.length})
                </button>
              </div>

              {/* Contenido pestaña 1: Turnos de Hoy */}
              {activeTab === 'TURNOS' && (
                <div className="space-y-2.5">
                  {unchargedAppointments.length === 0 ? (
                    <div className="py-10 text-center">
                      <p className="text-xs md:text-sm text-slate-400">
                        No hay turnos agendados para cobrar este día.
                      </p>
                    </div>
                  ) : (
                    unchargedAppointments.map((appt) => (
                      <div
                        key={appt.id}
                        className="p-3.5 rounded-2xl bg-slate-50 hover:bg-slate-100/80 border border-slate-200/80 flex items-center justify-between transition"
                      >
                        <div>
                          <div className="font-bold text-sm text-slate-900 flex items-center gap-1.5">
                            <User className="w-3.5 h-3.5 text-slate-400" />
                            {appt.clientName}
                          </div>
                          <div className="text-xs text-slate-500 mt-0.5">
                            {appt.startTime} hs · {appt.serviceName} · {appt.staffName}
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="font-black text-sm text-slate-800">
                            ${appt.price.toLocaleString('es-AR')}
                          </span>
                          <button
                            onClick={() => setSelectedAppt(appt)}
                            className="px-3 py-1.5 bg-[#047857] hover:bg-[#065f46] text-white text-xs font-bold rounded-xl shadow-sm transition"
                          >
                            Cobrar
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* Contenido pestaña 2: Venta de Productos */}
              {activeTab === 'PRODUCTOS' && (
                <div className="space-y-3.5">
                  <div className="max-h-48 overflow-y-auto space-y-1.5 pr-1">
                    {products.map((prod) => (
                      <div
                        key={prod.id}
                        className="p-2.5 rounded-xl border border-slate-200 bg-white flex items-center justify-between text-xs"
                      >
                        <div>
                          <div className="font-bold text-slate-800">{prod.name}</div>
                          <div className="text-[10px] text-slate-400">Stock: {prod.stock} un.</div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-black text-slate-700">${prod.salePrice?.toLocaleString('es-AR')}</span>
                          <button
                            type="button"
                            onClick={() => handleAddProduct(prod)}
                            disabled={prod.stock <= 0}
                            className="p-1 rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 disabled:opacity-40"
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  {productCart.length > 0 && (
                    <div className="p-3 bg-slate-50 rounded-2xl border border-slate-200/80 space-y-2">
                      <div className="text-[11px] font-bold text-slate-500 uppercase">
                        Ítems a cobrar ({productCart.length})
                      </div>
                      {productCart.map((it) => (
                        <div key={it.id} className="flex justify-between items-center text-xs">
                          <span>{it.quantity}x {it.name}</span>
                          <div className="flex items-center gap-2">
                            <span className="font-bold">${(it.price * it.quantity).toLocaleString('es-AR')}</span>
                            <button
                              onClick={() => handleRemoveProduct(it.id)}
                              className="text-rose-500 text-xs font-bold hover:underline"
                            >
                              ✕
                            </button>
                          </div>
                        </div>
                      ))}

                      <div className="pt-2 border-t border-slate-200 flex justify-between font-black text-sm text-slate-900">
                        <span>Total:</span>
                        <span>${productCart.reduce((acc, it) => acc + it.price * it.quantity, 0).toLocaleString('es-AR')}</span>
                      </div>

                      <div className="pt-2">
                        <label className="text-[11px] font-bold text-slate-500 block mb-1">Medio de Pago</label>
                        <select
                          value={productChannel}
                          onChange={(e) => setProductChannel(e.target.value)}
                          className="w-full text-xs p-2 bg-white border border-slate-200 rounded-xl"
                        >
                          <option value="EFECTIVO">💵 Efectivo</option>
                          <option value="DEBITO">💳 Débito</option>
                          <option value="CREDITO">💳 Crédito</option>
                          <option value="TRANSFERENCIA">🏦 Transferencia</option>
                          <option value="MERCADOPAGO">📱 Mercado Pago</option>
                        </select>
                      </div>

                      <button
                        onClick={handleConfirmProductSale}
                        disabled={submitting}
                        className="w-full py-2 bg-[#047857] hover:bg-[#065f46] text-white text-xs font-bold rounded-xl mt-2 transition"
                      >
                        {submitting ? 'Procesando...' : 'Confirmar Venta'}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
